import { useEffect, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import UFOSegment from '@atlaskit/react-ufo/segment';

import { templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data } from '@/__generated_contentful__/templatePaginatedSearchResultsContentfulRelayRefetchableFragment.graphql';
import { Adg4Button } from '@/components/adg4/button/src/button';
import { SkeletonTemplateCard } from '@/components/confluence/templates/skeleton-template-card';
import { ConfluenceTemplateMessages } from '@/components/confluence/templates/template-messages.ts';

import { TemplateCard } from './template-card-contentful-relay';

export interface TemplateCardGridProps {
	templates: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$data['templates'];
	hasNextPage: boolean;
	onLoadMore?: () => void;
	loadMorePending?: boolean;
	initialAnimation?: boolean;
}

const gridStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

const gridContainerStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	listStyle: 'none',
	listStylePosition: 'inside',
	paddingInlineStart: 0,
	margin: 0,
	gap: 64,
	'@media (min-width: 40rem) and (max-width: 63.99rem)': {
		width: '676px',
		gap: '32px',
	},
	justifyContent: 'center',
});

const gridItemStyles = css({
	listStyle: 'none',
	display: 'flex',
	flexWrap: 'wrap',
	opacity: 0,
	transform: 'translate3d(0, 60px, 0)',
});

const gridItemVisibleStyles = css({
	opacity: 1,
	transform: 'translate3d(0, 0, 0)',
});

const inViewAnimation = css({
	transition: 'opacity .3s ease-in-out, transform .3s ease-in-out',
	transitionDelay: 'calc(0.2s * var(--offset, 0))',
});

const buttonStyle = css({
	width: '210px',
	height: '44px',
	justifyContent: 'center',
	alignItems: 'center',
	display: 'flex',
	gap: '10px',
	padding: '16px',
});

export const TEMPLATE_CARD_BACKGROUNDS = [
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg1.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg2.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg3.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg4.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg5.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg6.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg7.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg8.svg)',
	'url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg9.svg)',
];

// NOTE: Implementation (mostly) copied from src/components/confluence/cfind-1795/template-card-grid.tsx

// eslint-disable-next-line complexity
export const TemplateCardGrid = ({
	templates,
	hasNextPage,
	onLoadMore,
	loadMorePending = false,
	initialAnimation = true,
}: TemplateCardGridProps) => {
	const [shouldShowCards, setShouldShowCards] = useState(!initialAnimation);
	const [shouldAnimate, setShouldAnimate] = useState(initialAnimation);

	const [pageOffset, setPageOffset] = useState(0);

	const [previousTemplates, setPreviousTemplates] = useState(templates);

	if (previousTemplates !== templates) {
		if (
			templates?.items.length &&
			previousTemplates?.items.length &&
			templates?.items.length <= previousTemplates?.items.length
		) {
			setPageOffset(0);
		} else {
			setPageOffset(previousTemplates?.items.length ?? 0);
		}
		setPreviousTemplates(templates);
		setShouldShowCards(false);
		setShouldAnimate(true);
	}

	useEffect(() => {
		let timeoutid: NodeJS.Timeout;
		if (!shouldShowCards) {
			timeoutid = setTimeout(() => {
				setShouldShowCards(true);
			}, 100);
		}
		return () => {
			clearTimeout(timeoutid);
		};
	}, [shouldShowCards]);

	return (
		<UFOSegment name="confluence-template-card-grid">
			<div css={gridStyles} data-testid="template-card-grid">
				{templates?.items.length === 0 ? (
					<div>
						<FormattedMessage {...ConfluenceTemplateMessages.noTemplatesAvailableText} />
					</div>
				) : (
					<>
						<ul css={gridContainerStyles}>
							{templates?.items.map((template, index) => (
								<li
									key={`${template?.sys?.id}-${index}`}
									css={[
										gridItemStyles,
										(shouldShowCards || index < pageOffset) && gridItemVisibleStyles,
										shouldShowCards && shouldAnimate && index >= pageOffset && inViewAnimation,
									]}
									style={
										{
											'--offset': index - pageOffset,
										} as React.CSSProperties
									}
									data-testid={`template-card-grid-item-${index}`}
								>
									<TemplateCard
										// @ts-ignore
										template={template}
										background={TEMPLATE_CARD_BACKGROUNDS[index % TEMPLATE_CARD_BACKGROUNDS.length]}
									/>
								</li>
							))}
							{loadMorePending &&
								Array.from(
									{
										length:
											// @ts-ignore
											3 - (templates?.items.length % 3),
									},
									(_, index) => (
										<li
											key={`placeholder-${
												// @ts-ignore
												templates?.items.length + index
											}`}
											css={[gridItemStyles, gridItemVisibleStyles]}
										>
											<SkeletonTemplateCard
												index={
													// @ts-ignore
													templates?.items.length + index
												}
												background={
													TEMPLATE_CARD_BACKGROUNDS[
														// @ts-ignore
														(templates?.items.length + index) % TEMPLATE_CARD_BACKGROUNDS.length
													]
												}
											/>
										</li>
									),
								)}
						</ul>

						{hasNextPage && (
							<Adg4Button
								variant="secondary"
								size="small"
								color={'black'}
								css={buttonStyle}
								style={{
									marginTop: '60px',
									textAlign: 'center',
								}}
								onClick={() => onLoadMore?.()}
								dataEventProps={{
									'data-product-key': 'confluence',
									'data-event': 'clicked',
									'data-event-component': 'linkButton',
									'data-event-container': 'templatesSection',
									'data-label': 'load-more-templates-button',
									'data-label-english': 'Load more templates',
									'data-track-event': 'confluence.load-more-templates.cta',
								}}
							>
								<FormattedMessage {...ConfluenceTemplateMessages.loadMoreTemplatesButtonText} />
							</Adg4Button>
						)}
					</>
				)}
			</div>
		</UFOSegment>
	);
};
